import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosSetup.js';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Swal from 'sweetalert2';


const EditProfile = ({employeeId}) => {
    const [employeeData, setEmployeeData] = useState(null);

    // State for password visibility
    const [isOldPasswordVisible, setOldPasswordVisible] = useState(false);
    const [isNewPasswordVisible, setNewPasswordVisible] = useState(false);
    const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    
    const fetchEmployee = async (employeeId) => {      
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/users/${employeeId}`)
            .then(response => {
                setEmployeeData(response.data.data);         
            })
            .catch(error => {
                console.log(error);
                setEmployeeData([]); 
            });
    }

    useEffect(() => {
        if (employeeId) {
            fetchEmployee(employeeId);
        }
    },[employeeId]);


    // VALIDATION RULES
    const schema = Yup.object().shape({
            name: Yup.string().required(),
            dob: Yup.date().nullable().typeError('dob is a required field')
                .required('dob is a required field').max(new Date(), 'date of birth cannot be in the future'),
            phone_number: Yup.string().required('phone is a required field')
                        .matches(/^\d{10}$/, 'phone must be exactly 10 digits'),
            home_phone: Yup.string().nullable().notRequired()
                            .when('home_phone', {
                                is: (value) => value?.length,
                                then: (rule) => rule.matches(/^\d{10}$/, 'home phone exactly 10 digits'),
                            }),
        },[
            ['home_phone', 'home_phone'],
        ]
    );
  
    const { register, handleSubmit, formState: { errors }, reset} = useForm({
        mode: 'all',
        resolver: yupResolver(schema)
    });


    // VALIDATION RULES
    const password_schema = Yup.object().shape({
        old_password: Yup.string().required('old password is a required field'),
        new_password: Yup.string().required('new password is a required field').min('6','new password must be at least 6 characters'),
            // .matches(
            // /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{5,}$/,
            // 'password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'),
        confirm_password: Yup.string().required('confirm password is a required field')
            .oneOf([Yup.ref('new_password'), null], 'passwords must match'),
    });

    const { register: registerfield , handleSubmit: handlePasswordSubmit, formState: { errors: error }} = useForm({
        mode: 'all',
        resolver: yupResolver(password_schema)
    });
    
    useEffect(() => {
        if (employeeData) {
            reset(employeeData);
        }
    }, [employeeData, reset]);

    const onSubmit = async (data) => {
        axiosInstance.post(`${process.env.REACT_APP_API_URL}/update_profile`, data)
            .then(response => {
                Swal.fire({icon:"success", text:response.data.message});
            })
            .catch(({response:{data}})=>{
                Swal.fire({text:data.message, icon:"error"});
                console.error('Error updating profile', data);
            });
    }

    const onPasswordSubmit = async (data) => {
        axiosInstance.post(`${process.env.REACT_APP_API_URL}/update_profile`, data)
            .then(response => {
                Swal.fire({icon:"success", text:"Successfully updated password" || response.data.message});
            })
            .catch(({response:{data}})=>{
                Swal.fire({text:data.message, icon:"error"});
                console.error('Error updating password', data);
            });
    }

    if (!employeeId || !employeeData) {
        return null;
    }

    // Toggle functions for each password field
    const toggleOldPassword = () => setOldPasswordVisible(prev => !prev);
    const toggleNewPassword = () => setNewPasswordVisible(prev => !prev);
    const toggleConfirmPassword = () => setConfirmPasswordVisible(prev => !prev);

    return (
        <div className="content container-fluid">
            <div className="page-header">
                <div className="row align-items-center">
                    <div className="col">
                        <h3 className="page-title">Profile</h3>
                        <ul className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/dashboard">Dashboard</Link></li>
                            <li className="breadcrumb-item active">Profile</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="card tab-box">
                <div className="card-body">
                    <ul className="nav nav-pills nav-style-2 mb-3 border-bottom" role="tablist">
                        <li className="nav-item" role="presentation">
                            <Link className="nav-link active" data-bs-toggle="tab" role="tab" aria-current="page" to="#profile"
                                aria-selected="true">Profile</Link>
                        </li>
                        <li className="nav-item" role="presentation">
                            <Link className="nav-link" data-bs-toggle="tab" role="tab" aria-current="page" to="#changePassword"
                                aria-selected="false" tabindex="-1">Change Password</Link>
                        </li>
                    </ul>

                    <div className="tab-content pt-0">
                        <div id="profile" className="tab-pane text-muted active show" role="tabpanel">
                            <div className="row">
                                <div className="col-md-12">
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <input type="hidden"  {...register('purpose')} value="updateProfile" />
                                        <div className="row">
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Employee ID</label>
                                                    <input className="form-control" type="text" disabled defaultValue={employeeData.emp_id} />
                                                </div>
                                            </div>                                
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Email</label>
                                                    <input className="form-control" type="text" disabled defaultValue={employeeData.email} />
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Joining Date</label>
                                                    <input className="form-control" type="date" disabled defaultValue={employeeData.joined_date} />
                                                </div>
                                            </div>                                
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Designation</label>
                                                    <input className="form-control" type="text" disabled defaultValue={employeeData.designation} />
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Name <span className="text-danger">*</span></label>
                                                    <input className="form-control" type="text" defaultValue={employeeData.name} {...register('name')} disabled/>
                                                    <div className="text-sm text-danger">
                                                        {errors.name && <p>{errors.name.message}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Date of Birth <span className="text-danger">*</span></label>
                                                    <input className="form-control" type="date" defaultValue={employeeData.dob || ''} {...register('dob')} disabled/>
                                                    <div className="text-sm text-danger">
                                                        {errors.dob && <p>{errors.dob.message}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-sm-12">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Address</label>
                                                    <textarea className="form-control" type="text" {...register('address')} disabled>{employeeData.address || ""}</textarea>
                                                </div>
                                            </div>                                            
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Personal Phone <span className="text-danger">*</span></label>
                                                    <input className="form-control" type="text" defaultValue={employeeData.phone_number || ""} {...register('phone_number')} disabled/>
                                                    <div className="text-sm text-danger">
                                                        {errors.phone_number && <p>{errors.phone_number.message}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Personal Email </label>
                                                    <input className="form-control" type="text" defaultValue={employeeData.personal_email || ""} {...register('personal_email')} disabled/>
                                                </div>
                                            </div>                                            
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Emergency Phone </label>
                                                    <input className="form-control" type="text" defaultValue={employeeData.home_phone || ""} {...register('home_phone')} disabled/>                                    
                                                    <div className="text-sm text-danger">
                                                        {errors.home_phone && <p>{errors.home_phone.message}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* <div className="submit-section">
                                            <button type="submit" className="btn btn-primary submit-btn">UPDATE</button>
                                        </div> */}
                                    </form>
                                </div>
                            </div>
                        </div>

                        <div id="changePassword" className="tab-pane text-muted" role="tabpanel">
                            <div className="row">
                                <div className="col-md-12 account-wrapper">
                                    <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                                        <input type="hidden"  {...register('purpose')} value="changePassword" />
                                        <div className="row">
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Old Password <span className="text-danger">*</span></label>
                                                    <div className="password-container">
                                                        <input className="form-control" type={isOldPasswordVisible ? 'text' : 'password'} {...registerfield('old_password')} />
                                                        <span className={`fa-solid ${isOldPasswordVisible ? 'fa-eye' : 'fa-eye-slash'}`}
                                                            onClick={toggleOldPassword}></span>
                                                    </div>
                                                    <div className="text-sm text-danger">
                                                        {error.old_password && <p>{error.old_password.message}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">New Password <span className="text-danger">*</span></label>
                                                    <div className="password-container">
                                                        <input className="form-control" type={isNewPasswordVisible ? 'text' : 'password'} {...registerfield('new_password')} />
                                                        <span className={`fa-solid ${isNewPasswordVisible ? 'fa-eye' : 'fa-eye-slash'}`}
                                                            onClick={toggleNewPassword}></span>
                                                    </div>
                                                    <div className="text-sm text-danger">
                                                        {error.new_password && <p>{error.new_password.message}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-sm-6">
                                                <div className="input-block mb-3">
                                                    <label className="col-form-label">Confirm Password <span className="text-danger">*</span></label>
                                                    <div className="password-container">                                                    
                                                        <input className="form-control" type={isConfirmPasswordVisible ? 'text' : 'password'} {...registerfield('confirm_password')} />
                                                        <span className={`fa-solid ${isConfirmPasswordVisible ? 'fa-eye' : 'fa-eye-slash'}`}
                                                            onClick={toggleConfirmPassword}></span>
                                                    </div>
                                                    <div className="text-sm text-danger">
                                                        {error.confirm_password && <p>{error.confirm_password.message}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="submit-section">
                                            <button type="submit" className="btn btn-primary submit-btn">UPDATE</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default EditProfile